import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PictureInPicture2, ChevronDown, ChevronUp } from "lucide-react";
import { DesignControls } from "@/components/erp/DesignControls";
import { WORK_KEYS, SPECIAL_KEYS, refreshDesign } from "@/lib/designTimer";
const EFF = [...WORK_KEYS, ...SPECIAL_KEYS];

const copyStyles = (w) => {
  [...document.styleSheets].forEach((ss) => {
    try {
      const s = w.document.createElement("style");
      s.textContent = [...ss.cssRules].map((r) => r.cssText).join("\n");
      w.document.head.appendChild(s);
    } catch {
      if (ss.href) { const l = w.document.createElement("link"); l.rel = "stylesheet"; l.href = ss.href; w.document.head.appendChild(l); }
    }
  });
};

export const useDesignMe = () => {
  const [me, setMe] = useState(null);
  const [skew, setSkew] = useState(0);
  const [now, setNow] = useState(Date.now());
  const load = useCallback(() => {
    api.get("/design/me").then(({ data }) => { setMe(data); setSkew(new Date(data.server_now).getTime() - Date.now()); }).catch(() => setMe(null));
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("design-refresh", load);
    return () => { clearInterval(t); window.removeEventListener("design-refresh", load); };
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(t); };
  }, []);
  const s = me?.session;
  const elapsed = s ? (now + skew - new Date(s.started_at).getTime()) / 1000 : 0;
  const closedWork = EFF.reduce((t, k) => t + ((me?.today || {})[k] || 0), 0);
  const workToday = closedWork + (s && EFF.includes(s.status) ? elapsed : 0);
  const ctrl = { overtime: !!me?.overtime, forgot: !!me?.forgot_stop, workEnd: me?.work_end, onOvertime: (on) => toggleOvertime(on) };
  return { me, session: s, elapsed, workToday, load, ctrl };
};

export const toggleOvertime = async (on) => {
  try { await api.post("/design/overtime", { on }); toast.success(on ? "Lembur aktif hari ini — pengingat Lupa Stop dimatikan" : "Lembur dimatikan"); refreshDesign(); }
  catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah lembur"); }
};

const useForgotNotify = (me) => {
  const forgot = !!me?.forgot_stop;
  const sid = me?.session?.id;
  useEffect(() => {
    if (!forgot || !sid) { return; }
    const key = `design-forgot-${sid}-${new Date().toDateString()}`;
    if (localStorage.getItem(key)) { return; }
    localStorage.setItem(key, "1");
    const msg = `Jam kerja selesai (${me.work_end}). Status ${me.session.status} masih berjalan — klik Stop atau Lembur.`;
    toast.warning("Lupa Stop?", { description: msg, duration: 15000 });
    if ("Notification" in window && Notification.permission === "granted") { try { new Notification("FOMU · Lupa Stop?", { body: msg }); } catch { /* ignore */ } }
  }, [forgot, sid, me]);
};

export const pickDesignStatus = async (status, wo_id, extra) => {
  if (["HELP", "OFFICE", "ASSEMBLY"].includes(status) && !extra) { window.focus(); window.dispatchEvent(new CustomEvent("design-special", { detail: { status } })); return; }
  if ("Notification" in window && Notification.permission === "default") { Notification.requestPermission().catch(() => {}); }
  try { await api.post("/design/status", { status, wo_id: wo_id || "", ...(extra || {}) }); refreshDesign(); return true; }
  catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); return false; }
};

export const DesignFloatBar = () => {
  const { me, session, elapsed, workToday, ctrl } = useDesignMe();
  useForgotNotify(me);
  const [pip, setPip] = useState(null);
  const [min, setMin] = useState(false);
  const pipRef = useRef(null);
  const openPip = useCallback(async (ev) => {
    if (pipRef.current) return;
    if (!window.documentPictureInPicture) { if (!ev?.detail?.silent) toast.info("Jendela selalu-di-atas hanya didukung Chrome/Edge di laptop. Tombol tetap tersedia di pojok kanan bawah."); return; }
    try {
      const w = await window.documentPictureInPicture.requestWindow({ width: 360, height: 200 });
      copyStyles(w);
      w.document.body.style.margin = "0";
      w.document.body.style.background = "#0f172a";
      w.addEventListener("pagehide", () => { pipRef.current = null; setPip(null); });
      pipRef.current = w; setPip(w);
    } catch { toast.error("Gagal membuka jendela mengambang"); }
  }, []);
  useEffect(() => {
    window.addEventListener("design-open-pip", openPip);
    return () => { window.removeEventListener("design-open-pip", openPip); };
  }, [openPip]);
  const { pathname } = useLocation();
  if (!me || (!session && !pip)) return null;
  const onPick = (st) => pickDesignStatus(st, session?.wo_id);
  const panel = (idp) => <DesignControls session={session} elapsed={elapsed} workToday={workToday} onPick={onPick} compact idp={idp} {...ctrl} />;
  return (
    <>
      {pip && createPortal(<div className="p-3">{panel("pip")}</div>, pip.document.body)}
      {!pip && !pathname.startsWith("/app/design-dashboard") && (
        <div className="fixed bottom-4 right-4 z-50 w-[340px] max-w-[calc(100vw-2rem)] bg-slate-900/95 backdrop-blur-md rounded-2xl shadow-2xl p-3 border border-slate-700" data-testid="design-float-bar">
          <div className="flex items-center justify-end gap-2 -mt-1 mb-1">
            <button type="button" onClick={() => openPip()} data-testid="design-float-pip" className="text-[10px] text-sky-300 hover:text-sky-200 inline-flex items-center gap-1"><PictureInPicture2 className="w-3.5 h-3.5" /> Jendela di atas</button>
            <button type="button" onClick={() => setMin(!min)} data-testid="design-float-min" className="text-slate-400">{min ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
          </div>
          {min ? <div className="text-white text-xs font-mono" data-testid="design-float-mini">{session?.status} · {session?.wo_code}</div> : panel("float")}
        </div>
      )}
    </>
  );
};
